<script setup lang="ts">
import type { JsonTokenType, JsonValue } from "./jsonValue";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import VIcon from "../Icon/VIcon.vue";
import { JSON_TOKEN_CLASS } from "./jsonColors";

defineOptions({
  name: "VJsonTreeNode",
});

/**
 * One value in the tree view of `VJsonEditor`: a primitive on one line, or an object or array that opens and closes.
 * `expandAll` is bumped by the editor's expand all and collapse all buttons; every node then follows it.
 */
const {
  name = null,
  value,
  depth = 0,
  initialDepth = 1,
  expandAll = null,
  last = true,
} = defineProps<{
  /** Key in the parent object or index in the parent array; null for the root. */
  name?: string | number | null
  value: JsonValue
  depth?: number
  /** Nodes above this depth start open. */
  initialDepth?: number
  /** The last expand all (true) or collapse all (false) request with its sequence number. */
  expandAll?: { open: boolean, sequence: number } | null
  /** Whether a comma follows the node. */
  last?: boolean
}>();

const { t } = useI18n();

const isArray = computed(() => Array.isArray(value));
const isBranch = computed(() => value !== null && typeof value === "object");
const entries = computed<[string | number, JsonValue][]>(() => {
  if (Array.isArray(value)) {
    return value.map((item, index) => [index, item]);
  }
  return value !== null && typeof value === "object" ? Object.entries(value) : [];
});

// A node created after an expand all or collapse all (inside a branch that opens later) follows that request.
const open = ref(expandAll ? expandAll.open : depth < initialDepth);
watch(() => expandAll?.sequence, () => {
  if (expandAll) {
    open.value = expandAll.open;
  }
});

const summary = computed(() => (isArray.value
  ? t("base.json_editor.items", { count: entries.value.length }, entries.value.length)
  : t("base.json_editor.keys", { count: entries.value.length }, entries.value.length)));

function primitiveType(item: JsonValue): JsonTokenType {
  if (item === null) {
    return "null";
  }
  switch (typeof item) {
    case "string": return "string";
    case "number": return "number";
    case "boolean": return "boolean";
    default: return "other";
  }
}

const primitiveText = computed(() => JSON.stringify(value));
const nameText = computed(() => (typeof name === "number" ? String(name) : JSON.stringify(name)));
</script>

<template>
  <div class="leading-5" :data-depth="depth">
    <div class="flex items-start gap-1">
      <button
        v-if="isBranch && entries.length > 0"
        type="button"
        class="-ml-4 flex size-4 shrink-0 items-center justify-center rounded text-gray-500 hover:text-gray-800 focus-visible:outline-2 focus-visible:outline-primary-500 dark:text-gray-400 dark:hover:text-gray-100"
        :aria-expanded="open"
        :aria-label="open ? t('base.json_editor.collapse') : t('base.json_editor.expand')"
        data-testid="json-tree-toggle"
        @click="open = !open"
      >
        <VIcon :name="open ? 'chevron-down' : 'chevron-right'" class="text-[10px]" />
      </button>
      <span class="min-w-0 break-all">
        <template v-if="name !== null">
          <span :class="typeof name === 'number' ? JSON_TOKEN_CLASS.number : JSON_TOKEN_CLASS.key" v-text="nameText" />
          <span :class="JSON_TOKEN_CLASS.punctuation" v-text="': '" />
        </template>
        <template v-if="isBranch">
          <span :class="JSON_TOKEN_CLASS.punctuation" v-text="isArray ? '[' : '{'" />
          <template v-if="!open || entries.length === 0">
            <span
              v-if="entries.length > 0"
              class="text-gray-400 italic dark:text-gray-500"
              data-testid="json-tree-summary"
              v-text="` ${summary} `"
            />
            <span :class="JSON_TOKEN_CLASS.punctuation" v-text="(isArray ? ']' : '}') + (last ? '' : ',')" />
          </template>
        </template>
        <template v-else>
          <span :class="JSON_TOKEN_CLASS[primitiveType(value)]" v-text="primitiveText" />
          <span v-if="!last" :class="JSON_TOKEN_CLASS.punctuation" v-text="','" />
        </template>
      </span>
    </div>
    <template v-if="isBranch && open && entries.length > 0">
      <div class="ml-4 border-l border-gray-200 pl-4 dark:border-dark-600">
        <VJsonTreeNode
          v-for="([key, item], index) in entries"
          :key="key"
          :name="key"
          :value="item"
          :depth="depth + 1"
          :initial-depth="initialDepth"
          :expand-all="expandAll"
          :last="index === entries.length - 1"
        />
      </div>
      <span :class="JSON_TOKEN_CLASS.punctuation" v-text="(isArray ? ']' : '}') + (last ? '' : ',')" />
    </template>
  </div>
</template>
